import { compareCodes } from '../util.js';
import { mapMetrics } from './metrics.js';
import {
  isTaskType,
  stepLabel,
  type KnowledgeCitation,
  type KnowledgeItemStatus,
  type MapModel,
  type MapStep,
  type StepType,
} from './model.js';
import { mapIssues, type MapCheck } from './validate.js';

/**
 * Future-state maps (E13-F09 §2, ADR-020 §3, Decision 62). A future map is
 * an E12 map with a vendor product. Everything here is computed from the
 * versions, never stored (rule 26): how each step changes against the
 * validated current state it was drawn from, which requirements a step
 * meets, which cited items have newer vendor information, and the counts.
 *
 * "Replaced" is expressed by keeping the current step's key (ADR-020): a
 * future step with a current step's key is that step, changed or not; a key
 * the current state does not have is new; a current key the future map does
 * not have is removed.
 */

export type FutureTag = 'unchanged' | 'changed' | 'automated' | 'new';

export interface FutureStepTag {
  tag: FutureTag;
  /** The step cites a Limitation, or a requirement response answered N or F. */
  workaround: boolean;
}

/** A step named for display: a removed current step, or a future step. */
export interface StepRef {
  key: string;
  name: string;
  type: StepType;
  lane: string;
  lane_name: string;
}

export interface FutureChangeTags {
  /** Every step of the future map, by key. */
  steps: Record<string, FutureStepTag>;
  /** Current steps the future map does not have, in the current map's order. */
  removed: StepRef[];
}

/** Work a person does, or did, in the current state. */
const PEOPLE_TYPES: readonly StepType[] = ['task', 'user_task', 'manual_task'];

/**
 * How each future step differs from the current state (ADR-020 T2):
 *
 * - `new`: no current step has its key.
 * - `automated`: the current step was work a person does (`task`,
 *   `user_task`, `manual_task`) and the future step is the system's
 *   (`service_task`).
 * - `changed`: other wording (compared after `normaliseKey`), another lane,
 *   another step type, or another system. Type and system are how a step is
 *   done, so a step moved from paper into the product is a change the
 *   vendor's evidence must support (rule 24).
 * - `unchanged`: otherwise.
 *
 * `workaround` marks a step citing a Limitation item or a requirement
 * response whose answer is N (not supported) or F (future).
 */
export function futureChangeTags(future: MapModel, current: MapModel): FutureChangeTags {
  const now = new Map(current.steps.map((s) => [s.key, s]));
  const steps: Record<string, FutureStepTag> = {};
  for (const step of future.steps) {
    steps[step.key] = { tag: tagOf(step, now.get(step.key)), workaround: isWorkaround(step) };
  }
  const kept = new Set(future.steps.map((s) => s.key));
  const laneNames = new Map(current.lanes.map((l) => [l.key, l.name]));
  return {
    steps,
    removed: current.steps
      .filter((s) => !kept.has(s.key))
      .map((s) => ({
        key: s.key,
        name: s.name,
        type: s.type,
        lane: s.lane,
        lane_name: laneNames.get(s.lane) ?? s.lane,
      })),
  };
}

function tagOf(step: MapStep, was: MapStep | undefined): FutureTag {
  if (!was) return 'new';
  if (PEOPLE_TYPES.includes(was.type) && step.type === 'service_task') return 'automated';
  if (
    normaliseKey(step.name) !== normaliseKey(was.name) ||
    step.lane !== was.lane ||
    step.type !== was.type ||
    normaliseKey(step.system ?? '') !== normaliseKey(was.system ?? '')
  )
    return 'changed';
  return 'unchanged';
}

/** The vendor knowledge a step cites. */
export function knowledgeCitations(step: Pick<MapStep, 'citations'>): KnowledgeCitation[] {
  return step.citations.filter((c): c is KnowledgeCitation => c.kind === 'vendor_knowledge');
}

/** A Limitation, or an answer of N (not supported) or F (future): the product needs a workaround. */
export function isWorkaround(step: Pick<MapStep, 'citations'>): boolean {
  return knowledgeCitations(step).some(
    (c) =>
      c.item_kind === 'limitation' ||
      (c.item_kind === 'requirement_response' &&
        (c.answer_code?.trim().toUpperCase() === 'N' ||
          c.answer_code?.trim().toUpperCase() === 'F')),
  );
}

/**
 * Rule 24: a task step that is new, changed or automated says the product
 * does something, so it cites a knowledge item or is an assumption.
 */
export function claimsProduct(step: Pick<MapStep, 'type'>, tag: FutureTag): boolean {
  return isTaskType(step.type) && tag !== 'unchanged';
}

/**
 * Marks as assumptions the steps that claim something of the product and
 * cite no knowledge item (rule 24). Changes `model` in place and returns the
 * keys it marked, in step order. Every save path runs it: the AI draft's
 * tie-back and every change set.
 */
export function markAssumptions(model: MapModel, current: MapModel): string[] {
  const tags = futureChangeTags(model, current).steps;
  const marked: string[] = [];
  for (const step of model.steps) {
    if (step.is_assumption === true) continue;
    if (!claimsProduct(step, tags[step.key]!.tag)) continue;
    if (knowledgeCitations(step).length > 0) continue;
    step.is_assumption = true;
    marked.push(step.key);
  }
  return marked;
}

// ---------------------------------------------------------------------------
// Checks
// ---------------------------------------------------------------------------

export const FUTURE_MAP_CHECKS = [
  'assumption_required',
  'assumption_with_citation',
  'knowledge_item_not_allowed',
] as const;
export type FutureMapCheck = (typeof FUTURE_MAP_CHECKS)[number];

export interface FutureMapContext {
  /** The validated current-state version the future version is drawn against. */
  current: MapModel;
  /**
   * The items a step may newly cite: confirmed, and of the map's product or
   * (company items) its vendor (rule 10).
   */
  allowedItemIds: ReadonlySet<string>;
  /**
   * The version this one is based on. A step keeps citing what it cited
   * there, whatever happened to the item since: the map pins it (rule 25).
   */
  base?: MapModel | null;
}

export type FutureMapCheckResult =
  | { valid: true }
  | { valid: false; check: MapCheck | FutureMapCheck; subject: string | null; message: string };

/**
 * ADR-017's checks, then (rules 10, 24): every task step that is new,
 * changed or automated cites a knowledge item or is an assumption, never
 * both; every cited item is allowed, or carried forward by the same step
 * from the base version. The first problem, in step order.
 */
export function validateFutureMap(model: MapModel, ctx: FutureMapContext): FutureMapCheckResult {
  const first = mapIssues(model)[0];
  if (first) return { valid: false, ...first };
  const issue = futureIssues(model, ctx)[0];
  return issue ? { valid: false, ...issue } : { valid: true };
}

export function futureIssues(
  model: MapModel,
  ctx: FutureMapContext,
): Array<{ check: FutureMapCheck; subject: string; message: string }> {
  const tags = futureChangeTags(model, ctx.current).steps;
  const before = new Map((ctx.base?.steps ?? []).map((s) => [s.key, s]));
  const issues: Array<{ check: FutureMapCheck; subject: string; message: string }> = [];
  for (const step of model.steps) {
    const cited = knowledgeCitations(step);
    if (step.is_assumption === true && cited.length > 0) {
      issues.push({
        check: 'assumption_with_citation',
        subject: step.key,
        message: `${stepLabel(step)} is marked an assumption and cites vendor evidence; it is one or the other.`,
      });
    }
    if (claimsProduct(step, tags[step.key]!.tag) && cited.length === 0 && !step.is_assumption) {
      issues.push({
        check: 'assumption_required',
        subject: step.key,
        message: `${stepLabel(step)} says the product does something but cites no vendor evidence: link a knowledge item or mark it an assumption.`,
      });
    }
    const kept = new Set(
      knowledgeCitations(before.get(step.key) ?? { citations: [] }).map((c) => c.item_id),
    );
    for (const c of cited) {
      if (ctx.allowedItemIds.has(c.item_id) || kept.has(c.item_id)) continue;
      issues.push({
        check: 'knowledge_item_not_allowed',
        subject: step.key,
        message: `${stepLabel(step)} cites ${c.item_number ?? 'an item'}, which is not a confirmed item of this product or its vendor.`,
      });
    }
  }
  return issues;
}

// ---------------------------------------------------------------------------
// Requirements met
// ---------------------------------------------------------------------------

export interface EngagementRequirementRef {
  id: string;
  /** `AP-006`, or a client-series `AP-C01`. */
  number: string;
  /** The library item it was loaded from; null for a client-series requirement. */
  library_item_id: string | null;
  /** Edited after loading: the vendor answered the library wording. */
  is_edited: boolean;
}

export interface RequirementMet {
  requirement_id: string;
  number: string;
  edited_after_load: boolean;
}

/**
 * The engagement requirements a step meets (ADR-020 Q2): those loaded from
 * the library item a cited requirement response answers. A requirement
 * edited after loading is marked, because the answer was to the library's
 * wording; a client-series requirement has no library item and never appears.
 */
export function requirementsMet(
  step: Pick<MapStep, 'citations'>,
  requirements: readonly EngagementRequirementRef[],
): RequirementMet[] {
  const answered = new Set(
    knowledgeCitations(step).flatMap((c) =>
      c.item_kind === 'requirement_response' && c.library_item_id ? [c.library_item_id] : [],
    ),
  );
  return requirements
    .filter((r) => r.library_item_id !== null && answered.has(r.library_item_id))
    .map((r) => ({ requirement_id: r.id, number: r.number, edited_after_load: r.is_edited }))
    .sort((a, b) => compareCodes(a.number, b.number));
}

// ---------------------------------------------------------------------------
// Newer vendor information (rule 25)
// ---------------------------------------------------------------------------

export interface KnowledgeItemState {
  id: string;
  number: string | null;
  status: KnowledgeItemStatus;
  flag_reason: string | null;
  /** The item this one replaced, if it is a successor. */
  supersedes_item_id: string | null;
}

export interface VendorInformationChange {
  step_key: string;
  item_id: string;
  item_number: string | null;
  state: 'superseded' | 'retired' | 'flagged';
  /** The confirmed item that replaces it now, following successors forward. */
  successor_id?: string;
  successor_number?: string | null;
  flag_reason?: string;
}

/**
 * The cited items with newer vendor information, per step, in step order:
 * superseded (with the confirmed head of its successors, when there is one),
 * retired, or flagged. `itemStates` holds the cited items and their
 * successors. The map is never changed by this: a redraft is offered.
 */
export function vendorInformationChanges(
  model: MapModel,
  itemStates: ReadonlyMap<string, KnowledgeItemState>,
): VendorInformationChange[] {
  const supersededBy = new Map<string, string>();
  for (const s of itemStates.values()) {
    if (s.supersedes_item_id && s.status !== 'proposed' && s.status !== 'declined')
      supersededBy.set(s.supersedes_item_id, s.id);
  }
  const changes: VendorInformationChange[] = [];
  for (const step of model.steps) {
    for (const c of knowledgeCitations(step)) {
      const state = itemStates.get(c.item_id);
      if (!state) continue;
      if (state.status === 'superseded') {
        const head = headOf(c.item_id, supersededBy, itemStates);
        changes.push({
          step_key: step.key,
          item_id: c.item_id,
          item_number: state.number,
          state: 'superseded',
          ...(head ? { successor_id: head.id, successor_number: head.number } : {}),
        });
      } else if (state.status === 'retired' || state.status === 'flagged') {
        changes.push({
          step_key: step.key,
          item_id: c.item_id,
          item_number: state.number,
          state: state.status,
          ...(state.status === 'flagged' && state.flag_reason
            ? { flag_reason: state.flag_reason }
            : {}),
        });
      }
    }
  }
  return changes;
}

/** Follows successors forward to the confirmed head, or null when the chain ends elsewhere. */
function headOf(
  id: string,
  supersededBy: ReadonlyMap<string, string>,
  states: ReadonlyMap<string, KnowledgeItemState>,
): KnowledgeItemState | null {
  const seen = new Set<string>([id]);
  let next = supersededBy.get(id);
  while (next && !seen.has(next)) {
    seen.add(next);
    const state = states.get(next);
    if (!state) return null;
    if (state.status !== 'superseded') return state.status === 'confirmed' ? state : null;
    next = supersededBy.get(next);
  }
  return null;
}

/** The steps a redraft may change: those citing an item with newer vendor information. */
export function affectedStepKeys(changes: readonly VendorInformationChange[]): string[] {
  return [...new Set(changes.map((c) => c.step_key))];
}

// ---------------------------------------------------------------------------
// Counts (rule 26)
// ---------------------------------------------------------------------------

export interface FutureMapMetrics {
  /** E12's `mapMetrics` on the future map: on its main path. */
  people: number;
  paper: number;
  system: number;
  handoffs: number;
  /** Task steps citing at least one knowledge item. */
  vendor_cited: number;
  /** Steps marked assumptions. */
  assumptions: number;
  /** Steps showing a workaround. */
  workarounds: number;
  /** Current steps the future map does not have. */
  removed: number;
  /** Distinct pain points on current steps that are removed, changed or automated. */
  pain_points_on_removed_or_changed: number;
}

/** The counts beside a future map, against the current state it was drawn from. Never stored. */
export function futureMapMetrics(future: MapModel, current: MapModel): FutureMapMetrics {
  const base = mapMetrics(future);
  const tags = futureChangeTags(future, current);
  const removed = new Set(tags.removed.map((s) => s.key));
  const touched = (key: string) => {
    const t = tags.steps[key]?.tag;
    return removed.has(key) || t === 'changed' || t === 'automated';
  };
  return {
    people: base.steps_people_do,
    paper: base.steps_on_paper,
    system: base.steps_system_does,
    handoffs: base.handoffs,
    vendor_cited: future.steps.filter((s) => isTaskType(s.type) && knowledgeCitations(s).length > 0)
      .length,
    assumptions: future.steps.filter((s) => s.is_assumption === true).length,
    workarounds: future.steps.filter((s) => tags.steps[s.key]!.workaround).length,
    removed: removed.size,
    pain_points_on_removed_or_changed: new Set(
      current.steps
        .filter((s) => touched(s.key))
        .flatMap((s) => s.pain_points.map((p) => p.pain_point_id)),
    ).size,
  };
}

// ---------------------------------------------------------------------------
// Item numbers
// ---------------------------------------------------------------------------

/** `VK-00412`, `vk-412` and `VK412` are one item: its number as stored, or null. */
export function knowledgeNumber(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const m = /^\s*VK-?0*(\d{1,9})\s*$/i.exec(value);
  if (!m) return null;
  const seq = Number(m[1]);
  return seq >= 1 ? formatKnowledgeItemNumber(seq) : null;
}

/**
 * Wording compared without case, punctuation or spacing (as the requirement
 * library's `normaliseKey`, kept here so the map engine imports nothing: the
 * web canvas runs it from source).
 */
function normaliseKey(value: string): string {
  return value
    .normalize('NFKC')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/** 412 → `VK-00412` (five digits or more), as numbers are issued (E13 rule 9). */
function formatKnowledgeItemNumber(seq: number): string {
  return `VK-${String(seq).padStart(5, '0')}`;
}
